import { useState, useEffect } from 'react';
import styled from 'styled-components';

import Box from './Box';
import { TTypography } from './types';

import { handleTypography } from '../../../styles/Shared/TypographyStyles';

type TInput = {
    iconPadding?: boolean;
    isInvalid?: boolean;
};

const InputWrapper = styled(Box) <TInput>`
    position: relative;
    display: inline-block;
    width: 100%;
    height: 100%;

    svg > path {
        fill: ${({ theme, isInvalid }) => !isInvalid ? null : theme.color['error-4']};
    }
`;

const StyledTextarea = styled.textarea<TTypography & TInput>`
    ${(props: TTypography) => handleTypography(props)}

    background: ${({ theme, isInvalid }) => !isInvalid ? theme.color['neutral1'] : theme.color['error-1']};
    border-radius: 8px;
    height: 100%;
    width: 100%;
    padding: ${({ iconPadding }) => (iconPadding ? '20px 42px 20px 16px' : '20px 16px')};
    border: none;
    outline: none;
    resize: none;
    overflow-y: auto; /* Enables vertical scrolling */

    &:not(:placeholder-shown) {
        ~ label {
            position: absolute;
            bottom: -20px; /* Position label below the textarea */
            left: 8px;
            font-size: 12px;
            font-weight: 400;
            color: ${({ theme }) => theme.color['coolgray-10']};
            z-index: 1; /* Ensure label stays on top */
            background-color: ${({ theme }) => theme.color['neutral1']}; /* Add background for readability */
            padding: 0 4px;
        }
    }

    &:placeholder-shown {
        ~ label {
            visibility: hidden;
        }
    }

    &::placeholder {
        color: ${({ theme }) => theme.color['coolgray-11']};
    }

    &:hover,
    &:focus {
        background: ${({ theme, isInvalid }) => !isInvalid ? theme.color['neutral2'] : theme.color['error-2']};
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-2']};
        outline: none;
    }
`;
const Label = styled.span<TTypography & TInput>`
    ${(props: TTypography) => handleTypography(props)}

    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.color['coolgray-10']};
    background: ${({ theme }) => theme.color['neutral1']};
    padding: 0 4px;
`;

const SideWrapper = styled.div`
    position: absolute;
    top: 20px;
    right: 12px;
`;

const Value = styled.span<TTypography>`
    ${(props: TTypography) => handleTypography(props)}

    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.color['coolgray-11']};
`;

const LabelWrapper = styled.div<TTypography & TInput>`
    position: absolute;
    bottom: -20px; /* Move the entire label wrapper to the bottom */
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between; /* Align label and character count */
    align-items: center;
    pointer-events: none;

    ${StyledTextarea}:not(:placeholder-shown) ~ & {
        visibility: visible;
        transform: translateY(0); /* Adjust visibility when active */
    }
`;

type TProps = {
    label?: string;
    sideComponent?: React.ReactElement;
    register?: any;
    errors?: any;
    id: string;
    infoMessage?: string;
    maxLength?: number;
};

const TextArea = ({ label, placeholder, sideComponent, onChange, onBlur, register, errors, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & TProps) => {
    const [count, setCount] = useState(0);
    let hasSideComponent;
    let isInvalid = errors ? !!errors[props.id] : false;

    // Reset count if value already exists
    useEffect(() => {
        if (props.value) {
            let value = props.value + '';
            setCount(value.length);
        }
    }, [props.value]);

    const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCount(e.target.value.length);
        register.onChange(e);
    }

    let updatedRegister = {
        ...register,
        onChange: handleOnChange,
    }

    if (sideComponent) hasSideComponent = true;

    return (
        <>
            <InputWrapper isInvalid={isInvalid} mb={isInvalid ? 'sm' : undefined}>
                <StyledTextarea
                    placeholder={placeholder}
                    required={props.required}
                    typography="text-m-light"
                    iconPadding={hasSideComponent}
                    isInvalid={isInvalid}
                    {...updatedRegister}
                    {...props}
                />
                <LabelWrapper typography="ui-m" isInvalid={isInvalid}>
                    <Label>{label}</Label>
                    <Value>{props.maxLength ? `${count} / ${props.maxLength}` : ''}</Value>
                </LabelWrapper>

                {sideComponent ? <SideWrapper>{sideComponent}</SideWrapper> : null}
            </InputWrapper>

            {/* Info Message */}
            {props.infoMessage ? (
                <Box display="flex" textAlign="left" as="span" typography="ui-xs" customColor="coolgray-10">
                    {props.infoMessage}
                </Box>
            ) : null}

            {/* Error Message */}
            {errors && errors[props.id]?.message ? (
                <Box display="flex" textAlign="left" as="span" typography="ui-s" customColor="error-4" ml="sm">
                    {errors[props.id].message}
                </Box>
            ) : null}
        </>
    );
};

export default TextArea;
